/**
 * TdeeCalibrationBadge — Gap C UI.
 *
 * Card discreto que só aparece quando houve calibração TDEE nos últimos 7 dias.
 * Explica ao utilizador porque é que o plano mudou (delta real vs previsto)
 * de forma humanizada.
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Scale, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

function daysSince(iso) {
  if (!iso) return Infinity;
  try {
    return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
  } catch {
    return Infinity;
  }
}

export default function TdeeCalibrationBadge({ program = "keto_fusion" }) {
  const [entry, setEntry] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/programs/${program}/calibration-history?limit=1`, { headers: headers() });
        const data = await res.json();
        if (cancelled) return;
        const last = (data.history || [])[0];
        // Only show if within last 7 days and factor actually changed
        if (last && daysSince(last.date) <= 7 && Math.abs(last.step || 0) > 0.001) {
          setEntry(last);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [program]);

  if (!entry) return null;

  const actualFaster = (entry.delta_actual ?? 0) < (entry.delta_predicted ?? 0);
  const summary = actualFaster
    ? "Estás a perder mais rápido do que previa"
    : "Estás a evoluir mais devagar do que previa";
  const stepStr = (entry.step > 0 ? "+" : "") + entry.step.toFixed(2);

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => setExpanded((v) => !v)}
      className="w-full text-left p-4 rounded-2xl border transition-all mb-4"
      style={{
        borderColor: "rgba(56,189,248,0.35)",
        background: "linear-gradient(140deg, rgba(56,189,248,0.10) 0%, rgba(9,9,11,0.95) 60%)",
        boxShadow: "0 4px 18px rgba(56,189,248,0.15), inset 0 1px 0 rgba(56,189,248,0.2)",
      }}
      data-testid="tdee-calibration-badge"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: "radial-gradient(circle, rgba(56,189,248,0.35) 0%, rgba(56,189,248,0.10) 70%)",
            boxShadow: "0 0 14px rgba(56,189,248,0.45), inset 0 0 10px rgba(56,189,248,0.25)",
          }}
        >
          <Scale className="w-4 h-4 text-sky-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-sky-400">Plano ajustado ao teu ritmo</p>
            <ChevronRight className={`w-4 h-4 text-sky-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </div>
          <p className="text-sm font-bold text-zinc-100 mt-0.5 leading-tight">{summary}</p>
          {!expanded && (
            <p className="text-[10px] text-zinc-500 mt-1">Toca para ver os números</p>
          )}
        </div>
      </div>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 pt-3 border-t border-sky-500/20 grid grid-cols-2 gap-3 text-[11px]"
        >
          <div>
            <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Real (kg/sem)</p>
            <p className="text-lg font-black text-zinc-100 tabular-nums">{entry.delta_actual?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Previsto (kg/sem)</p>
            <p className="text-lg font-black text-zinc-100 tabular-nums">{entry.delta_predicted?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Activity factor</p>
            <p className="text-sm font-bold text-zinc-100 tabular-nums">
              {entry.old_factor?.toFixed(2)} → <span className="text-sky-400">{entry.new_factor?.toFixed(2)}</span>
            </p>
          </div>
          <div>
            <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Ajuste</p>
            <p className={`text-sm font-bold tabular-nums ${entry.step > 0 ? "text-emerald-400" : "text-amber-400"}`}>{stepStr}</p>
          </div>
          <p className="col-span-2 text-[10px] text-zinc-500 mt-1 italic leading-snug">
            Os teus macros diários já refletem este ajuste. Baseado em {entry.avg_kcal?.toFixed(0)} kcal/dia médios e BMR {entry.bmr?.toFixed(0)} kcal.
          </p>
        </motion.div>
      )}
    </motion.button>
  );
}
